import type { MouseEvent as ReactMouseEvent, RefObject } from "react";
import { useEffect, useRef, useState } from "react";

import { clamp } from "lodash-es";

import {
  MAX_STRING_CELL_HEIGHT,
  MIN_STRING_CELL_HEIGHT,
} from "src/cases/caseView/blocks/cellTypes";

type UseVerticalCellResizeOptions = {
  cellRef: RefObject<HTMLElement | null>;
  onHeightChange: (height: number) => void;
};

type UseVerticalCellResizeResult = {
  isResizing: boolean;
  handleResizeStart: (event: ReactMouseEvent) => void;
};

/** Drag-to-resize for a single table cell. The height is owned by the
 *  caller (it outlives edit mode) and reported through onHeightChange. */
export const useVerticalCellResize = ({
  cellRef,
  onHeightChange,
}: UseVerticalCellResizeOptions): UseVerticalCellResizeResult => {
  const [isResizing, setIsResizing] = useState(false);
  const dragStartRef = useRef({ clientY: 0, height: 0 });
  const frameRef = useRef<number | null>(null);

  const onHeightChangeRef = useRef(onHeightChange);
  onHeightChangeRef.current = onHeightChange;

  const handleResizeStart = (event: ReactMouseEvent) => {
    const cell = cellRef.current;
    if (!cell) return;
    // Keeps the drag from selecting text or reaching the cell's own
    // mousedown handlers.
    event.preventDefault();
    event.stopPropagation();
    // The handle sits at the bottom of the positioned ancestor (the <td>),
    // which a neighboring cell can stretch taller than this cell's content.
    // Baseline the drag on that visible height so the grip tracks the mouse.
    const visibleCell = cell.offsetParent ?? cell;
    dragStartRef.current = {
      clientY: event.clientY,
      height: visibleCell.getBoundingClientRect().height,
    };
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;

    // A plain click or jitter must not commit a height (it would swap a
    // read-mode cell to its resized rendering). Once a real drag starts,
    // every position tracks — including back near the origin.
    const DRAG_THRESHOLD_PX = 3;
    let dragEngaged = false;

    const applyHeight = (event: MouseEvent) => {
      const delta = event.clientY - dragStartRef.current.clientY;
      if (!dragEngaged && Math.abs(delta) < DRAG_THRESHOLD_PX) return;
      dragEngaged = true;
      onHeightChangeRef.current(
        clamp(
          dragStartRef.current.height + delta,
          MIN_STRING_CELL_HEIGHT,
          MAX_STRING_CELL_HEIGHT,
        ),
      );
    };

    // Coalesce to one update per frame: mousemove outpaces the browser's
    // paint rate, and each update makes the open editor remeasure itself.
    const handleMouseMove = (event: MouseEvent) => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        applyHeight(event);
      });
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      // Apply the final position so a cancelled frame doesn't lose it.
      applyHeight(event);
      setIsResizing(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "s-resize";
    document.body.style.userSelect = "none";

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  return { isResizing, handleResizeStart };
};
